/** React app path prefix for the main (studio) shell. */
export const STUDIO_BASE = '/ux/studio';

/** Default post-login / studio home. */
export const STUDIO_HOME = `${STUDIO_BASE}/Dashboard`;

/**
 * The Classic ASP dashboard — legacy's own landing page, `/Dashboard/?SessionKey=<guid>` in
 * `UserLoginAsResolveLandingPage` (`inc/functions_security.asp:602`).
 *
 * No `SessionKey` here: sign-in has already written legacy's `sid<n>` cookie for this hostname
 * (`persistSessionKey`, `services/auth.ts`), which is the same reason the profile menu's "back to
 * legacy" navigates to a bare `/Dashboard`. The trailing slash matches legacy's own redirect.
 *
 * Where client user types land at sign-in for the parallel-run window — see
 * `pages/login/post-login-destination.ts`.
 */
export const LEGACY_DASHBOARD = '/Dashboard/';

/**
 * Native React billing page (`pages/billing`, route `Billing` in `studio-routes.tsx`, the nav's
 * "Billing" entry). It is the React counterpart of legacy `PAYMENTS_SCREEN`
 * (`/brand/paymenttypes.asp`, `inc/constmin.asp:100`) — the profile menu's legacy map pairs
 * `/ux/billing` with `/Brand/paymenttypes.asp` — and is where a suspended business's Owner lands at
 * sign-in (ROBO-478, `pages/login/post-login-destination.ts`).
 */
export const STUDIO_BILLING = `${STUDIO_BASE}/Billing`;

/**
 * The brand Integration Hub (route `Business/IntegrationHub` in `studio-routes.tsx`).
 *
 * ★ DO NOT CHANGE WITHOUT THE API: it is the `returnPath` an OAuth connect and AutoHDR's hand-off
 * send the user back to, and the API's own default for the latter is the same string —
 * `AutoHdrHandoff.DEFAULT_RETURN_PATH` in HDPhotoHubAPI
 * (`API/Services/Integrations/Enable/IntegrationEnableModels.cs`).
 */
export const STUDIO_BUSINESS_INTEGRATION_HUB = `${STUDIO_BASE}/Business/IntegrationHub`;

/** A user's own integrations (route `Account/AppIntegrations` in `studio-routes.tsx`). */
export const STUDIO_ACCOUNT_APP_INTEGRATIONS = `${STUDIO_BASE}/Account/AppIntegrations`;

/** The Groups list (route `Groups` in `studio-routes.tsx`). `?groupId=` opens that group's drawer. */
export const STUDIO_GROUPS = `${STUDIO_BASE}/Groups`;

/** The router pattern of {@link studioGroupAppsPath}, relative to the studio base. */
export const STUDIO_GROUP_APPS_ROUTE = 'Groups/:groupId/Apps';

/**
 * One group's integrations (M1.2h, ROBO-630) — `/ux/studio/Groups/{groupId}/Apps`.
 *
 * ★ THE GROUP ID IS A PATH SEGMENT ON PURPOSE. This string is the `returnPath` an OAuth connect
 * started on the page comes back to, and a plain path survives both validators
 * (`IntegrationOAuthReturnPath.IsValid` in the API, `safeReturnPath` here) with nothing to encode.
 */
export const studioGroupAppsPath = (groupId: number): string => `${STUDIO_GROUPS}/${groupId}/Apps`;

/**
 * The Integration Hub's OAuth return page (ROBO-633, spec M4.2 §8.3.3).
 *
 * ★ DO NOT CHANGE WITHOUT THE API AND EVERY VENDOR CONSOLE: this exact path, on the one fixed host
 * per environment, is the redirect URL registered with each OAuth vendor, and the API builds it
 * from `IntegrationOAuthCallback.PATH` (HDPhotoHubAPI). `worker/index.ts` matches the same string to
 * send the page's `Referrer-Policy`.
 */
export const STUDIO_OAUTH_CALLBACK = `${STUDIO_BASE}/oauth/callback`;

/**
 * Hub login page. ★ DO NOT CHANGE WITHOUT THE API: Google sign-in (UX-802 / API-506) sends only a
 * `returnHost`, and the API builds the return URL itself as `https://{returnHost}/ux/login` from its
 * own matching constant — `GoogleSsoReturnHost.LOGIN_PATH` in HDPhotoHubAPI
 * (`API/Services/Authentication/GoogleSso/GoogleSsoReturnHost.cs`). The return handler here
 * (`consumeGoogleSsoReturn` in `services/google-sso.ts`) only accepts a return on this path or
 * `HUB_REGISTER_PATH`.
 */
export const HUB_LOGIN_PATH = '/ux/login';

/** Password reset request screen (UX-798) — see `hub-routes.tsx` and `pages/login/PasswordResetRequest.tsx`. */
export const HUB_FORGOT_PASSWORD_PATH = '/ux/forgot-password';

/**
 * Registration screen (UX-797) — see `hub-routes.tsx` and `pages/login/Register.tsx`.
 *
 * ★ DO NOT CHANGE WITHOUT THE API: Google sign-up (UX-803) returns here. The browser sends only
 * `returnHost` + `intent=signup`, and the API builds `https://{returnHost}/ux/register` from its own
 * matching constant — `GoogleSsoReturnHost.REGISTER_PATH` in HDPhotoHubAPI
 * (`API/Services/Authentication/GoogleSso/GoogleSsoReturnHost.cs`).
 */
export const HUB_REGISTER_PATH = '/ux/register';

/**
 * Set-password screen (UX-799), reached from a password-reset or registration-confirmation
 * email. `id` (query param) is the reset guid — matches the `?id={guid}` shape the live
 * reset email already links to today (`Login/password.asp?id={guid}`, `EmailController.cs`),
 * just on the new route instead of the legacy page. See `pages/login/SetPassword.tsx`.
 */
export const HUB_SET_PASSWORD_PATH = '/ux/set-password';

/**
 * The MCP connect / consent screen (`pages/hub/hub-mcp-connect-page.tsx`).
 *
 * ★ DO NOT CHANGE WITHOUT THE MCP WORKER: `Cloudflare/mcp` sends customers here from its own
 * `CONNECT_URL` binding, so a rename here strands anyone mid-connect until that worker is
 * redeployed. The contract between the two is `Cloudflare/mcp/docs/connect-handoff.md`.
 */
export const HUB_MCP_CONNECT_PATH = '/ux/connect';

/**
 * The reschedule page (ROBO-616, `pages/hub/hub-reschedule-page.tsx`) — the React counterpart of
 * legacy `Dashboard/reschedule.asp`. `guid` is the site's default download guid: present on a
 * guest's link, absent for a signed-in user.
 *
 * In the HUB shell, not the studio: a guest has no session and must not be bounced by (or made to
 * download) the studio shell. Legacy entry points — `/r/{task}/{guid}`, the dashboard link, the
 * mobile app's `rescheduleUrl` — are NOT repointed here yet; that is ROBO-695.
 */
export const HUB_RESCHEDULE_PATH = '/ux/reschedule/:orderId/:taskId/:guid?';

export function hubReschedulePath(orderId: number, taskId: number, guid?: string): string {
    const tail = guid ? `/${encodeURIComponent(guid)}` : '';
    return `/ux/reschedule/${orderId}/${taskId}${tail}`;
}

/** Reports hub (M1.5b) — the native menu that replaced the `Brand/reports.asp` iframe. */
export const STUDIO_REPORTS = `${STUDIO_BASE}/Reports`;

/**
 * The single route that hosts EVERY not-yet-migrated report in its legacy iframe
 * (`src/pages/reports/legacy-report.tsx`). `reportKey` is a `ReportEntry.id` from
 * `src/pages/reports/report-catalog.ts`; migrating a report removes it from this route
 * automatically, because the host only renders keys whose catalogue target is still `legacy`.
 */
export function reportsLegacyPath(reportKey: string): string {
    return `${STUDIO_REPORTS}/legacy/${reportKey}`;
}

/**
 * One "Private Report" (`tblAdhoc`) runner — M1.5c, replacing `Brand/Reports/Adhoc.asp?nAdhocID=`.
 * `adhocId` is the row's own id, not a `ReportEntry.id`: these rows are operator data, so they are
 * never in the static catalogue and never reach {@link reportsLegacyPath}.
 */
export function reportsAdhocPath(adhocId: number | string): string {
    return `${STUDIO_REPORTS}/adhoc/${adhocId}`;
}

/**
 * Shoot Calendar — M1.5d-1 (ROBO-265), replacing `Brand/Reports/ShootCalendar.asp`.
 *
 * ★ A STATIC CATALOGUE ENTRY, unlike {@link reportsAdhocPath}: `shoot-calendar` is one of the 35
 * rows in `src/pages/reports/report-catalog.ts`, and its `target` names exactly this path.
 */
export const STUDIO_REPORTS_SHOOT_CALENDAR = `${STUDIO_REPORTS}/ShootCalendar`;

export interface ShootCalendarPathOptions {
    /** 1-12. Omitted ⇒ the API answers with the BRAND's current month (never the browser's). */
    month?: number;
    year?: number;
    /**
     * Where the page's back link should go. Validated by `resolveBackTo` before it is rendered —
     * see `src/routes/back-to.ts` for why legacy's `sBackToList` is not ported as-is.
     */
    backTo?: string;
}

/** The Shoot Calendar route, optionally pinned to a month and carrying a back destination. */
export function reportsShootCalendarPath(options?: ShootCalendarPathOptions): string {
    const params = new URLSearchParams();

    if (options?.month != null) {
        params.set('month', String(options.month));
    }
    if (options?.year != null) {
        params.set('year', String(options.year));
    }
    if (options?.backTo) {
        params.set('backTo', options.backTo);
    }

    const query = params.toString();
    return query ? `${STUDIO_REPORTS_SHOOT_CALENDAR}?${query}` : STUDIO_REPORTS_SHOOT_CALENDAR;
}

/**
 * Availability Calendar — M1.5d-2 (ROBO-269), replacing `Brand/Reports/AvailabilityCalendar.asp`.
 *
 * ★ A STATIC CATALOGUE ENTRY, like {@link STUDIO_REPORTS_SHOOT_CALENDAR}: `availability-calendar`
 * is one of the rows in `src/pages/reports/report-catalog.ts`, and its `target` names exactly this
 * path. The segment keeps legacy's casing, matching the `ShootCalendar` precedent.
 */
export const STUDIO_REPORTS_AVAILABILITY_CALENDAR = `${STUDIO_REPORTS}/AvailabilityCalendar`;

export interface AvailabilityCalendarPathOptions {
    /** 1-12. Omitted ⇒ the API answers with the BRAND's current month (never the browser's). */
    month?: number;
    year?: number;
    /**
     * Appointment length in minutes — 15/30/60/90/120 (`AvailabilityCalendar.asp:43`). It rides on
     * the URL rather than living in component state because legacy carries `d` through its own
     * prev/next month links (`:260-261`), so a month step must not silently reset it — and because
     * a chart of "who is free for a 2-hour job in September" is a thing worth being able to link to.
     */
    duration?: number;
    /**
     * Where the page's back link should go. Validated by `resolveBackTo` before it is rendered —
     * see `src/routes/back-to.ts` for why legacy's `sBackToList` is not ported as-is.
     */
    backTo?: string;
}

/** The Availability Calendar route, optionally pinned to a month, duration and back destination. */
export function reportsAvailabilityCalendarPath(options?: AvailabilityCalendarPathOptions): string {
    const params = new URLSearchParams();

    if (options?.month != null) {
        params.set('month', String(options.month));
    }
    if (options?.year != null) {
        params.set('year', String(options.year));
    }
    if (options?.duration != null) {
        params.set('duration', String(options.duration));
    }
    if (options?.backTo) {
        params.set('backTo', options.backTo);
    }

    const query = params.toString();
    return query
        ? `${STUDIO_REPORTS_AVAILABILITY_CALENDAR}?${query}`
        : STUDIO_REPORTS_AVAILABILITY_CALENDAR;
}

/**
 * Scheduled Delivery/Completion Emails — M1.5e slice A (ROBO-263), replacing
 * `Brand/Reports/ScheduledEmails.asp`.
 *
 * ★ A STATIC CATALOGUE ENTRY, like {@link STUDIO_REPORTS_SHOOT_CALENDAR}: `scheduled-emails` is one
 * of the rows in `src/pages/reports/report-catalog.ts`, and its `target` names exactly this path.
 * There is no path helper because the report takes NO parameters at all — no filter, no range and
 * no paging, matching the legacy page and the endpoint.
 */
export const STUDIO_REPORTS_SCHEDULED_EMAILS = `${STUDIO_REPORTS}/ScheduledEmails`;

/**
 * iGUIDE Deactivation Report — M1.5e slice A (ROBO-263), replacing `Brand/Reports/iGuide.asp`.
 *
 * ★ A STATIC CATALOGUE ENTRY, as above. The segment keeps legacy's `iGUIDE` capitalisation in the
 * spirit of the `ShootCalendar` precedent while staying a legal path segment.
 */
export const STUDIO_REPORTS_IGUIDE_DEACTIVATION = `${STUDIO_REPORTS}/iGuideDeactivation`;

/**
 * The iGUIDE report, optionally pinned to a window.
 *
 * ★ `days` IS A STRING, NOT A NUMBER, AND THAT IS DELIBERATE. Legacy defaults a non-numeric value
 * to 30 rather than rejecting it (`iGuide.asp:15`) and the endpoint binds it as text to reproduce
 * that exactly, so a link may legitimately carry a value this app would not have generated.
 */
export function reportsIGuideDeactivationPath(days?: string | number): string {
    const value = typeof days === 'number' ? String(days) : days?.trim();

    return value
        ? `${STUDIO_REPORTS_IGUIDE_DEACTIVATION}?${new URLSearchParams({ days: value }).toString()}`
        : STUDIO_REPORTS_IGUIDE_DEACTIVATION;
}

/**
 * Copyright License Acceptance List — M1.5e slice B (ROBO-263), replacing
 * `Brand/Reports/LicenseAccepts.asp`.
 *
 * ★ A STATIC CATALOGUE ENTRY, as above: `license-accepts` is one of the rows in
 * `src/pages/reports/report-catalog.ts`, and its `target` names exactly this path.
 *
 * ★ THE ROUTE SEGMENT MATCHES THE `.asp` FILE NAME, NOT EITHER ON-SCREEN STRING — and there are two
 * of those, which disagree: the hub links to "Copyright License Acceptance List" while the page's
 * own heading reads "Copyright License Acceptance Acknowledgements" (`LicenseAccepts.asp:86`). Both
 * are legacy's and both are kept; see the page header.
 */
export const STUDIO_REPORTS_LICENSE_ACCEPTS = `${STUDIO_REPORTS}/LicenseAccepts`;

/**
 * The licence acceptance list, optionally pinned to a window.
 *
 * ★ `days` IS A STRING, NOT A NUMBER, for the same reason {@link reportsIGuideDeactivationPath}'s
 * is: legacy defaults a non-numeric value (to **90** here, `LicenseAccepts.asp:16`) rather than
 * rejecting it, and the endpoint binds it as text to reproduce that.
 */
export function reportsLicenseAcceptsPath(days?: string | number): string {
    const value = typeof days === 'number' ? String(days) : days?.trim();

    return value
        ? `${STUDIO_REPORTS_LICENSE_ACCEPTS}?${new URLSearchParams({ days: value }).toString()}`
        : STUDIO_REPORTS_LICENSE_ACCEPTS;
}

/**
 * Email Tracking Report — M1.5e slice B (ROBO-263), replacing `Brand/Reports/EmailTracking.asp`.
 *
 * ★ A STATIC CATALOGUE ENTRY, as above.
 */
export const STUDIO_REPORTS_EMAIL_TRACKING = `${STUDIO_REPORTS}/EmailTracking`;

export interface EmailTrackingPathOptions {
    /** The window in days. A STRING is legitimate — see {@link reportsLicenseAcceptsPath}. */
    days?: string | number;
    /**
     * ★★ OMITTED OR `false` MEANS "NEVER-OPENED MESSAGES ONLY", NOT "ALL MESSAGES". ★★ It is the
     * default and it narrows the report (`EmailTracking.asp:30` adds `having sum(nHits) is null`);
     * passing `true` WIDENS it. The flag is written onto the URL only when true, because absent and
     * false are the same request.
     */
    includeOpened?: boolean;
}

/** The Email Tracking report, optionally pinned to a window and the include-opened filter. */
export function reportsEmailTrackingPath(options?: EmailTrackingPathOptions): string {
    const params = new URLSearchParams();
    const days = typeof options?.days === 'number' ? String(options.days) : options?.days?.trim();

    if (days) {
        params.set('days', days);
    }
    if (options?.includeOpened) {
        params.set('includeOpened', 'true');
    }

    const query = params.toString();
    return query ? `${STUDIO_REPORTS_EMAIL_TRACKING}?${query}` : STUDIO_REPORTS_EMAIL_TRACKING;
}

/**
 * Scheduled Orders Report — M1.5e slice C (ROBO-263), replacing `Brand/Reports/ScheduledOrders.asp`.
 *
 * ★ A STATIC CATALOGUE ENTRY, as above: `scheduled-orders` is one of the rows in
 * `src/pages/reports/report-catalog.ts`, and its `target` names exactly this path.
 */
export const STUDIO_REPORTS_SCHEDULED_ORDERS = `${STUDIO_REPORTS}/ScheduledOrders`;

export interface ScheduledOrdersPathOptions {
    /**
     * The window's lower bound, as TEXT — legacy's own input format is `M/d/yyyy h:mm tt`
     * (`ScheduledOrders.asp:25`) and the endpoint parses with `InvariantCulture`.
     *
     * ★ EACH BOUND IS OPTIONAL AND DEFAULTS INDEPENDENTLY, server-side, from BRAND-today. A link
     * carrying only `start` means "from there until today 5:00 PM" — which is why neither is
     * synthesised here from the browser's clock.
     */
    start?: string;
    /** @see start */
    end?: string;
}

/** The Scheduled Orders report, optionally pinned to a window. */
export function reportsScheduledOrdersPath(options?: ScheduledOrdersPathOptions): string {
    const params = new URLSearchParams();
    const start = options?.start?.trim();
    const end = options?.end?.trim();

    if (start) {
        params.set('start', start);
    }
    if (end) {
        params.set('end', end);
    }

    const query = params.toString();
    return query ? `${STUDIO_REPORTS_SCHEDULED_ORDERS}?${query}` : STUDIO_REPORTS_SCHEDULED_ORDERS;
}

/**
 * Abandoned Carts Report — M1.5e slice C (ROBO-263), replacing `Brand/Reports/AbandonedCarts.asp`.
 *
 * ★ A STATIC CATALOGUE ENTRY, as above. ★ THE LABEL AND THE PAGE HEADING DISAGREE AGAIN, as they do
 * on two of this panel's slice-B siblings: the hub links it "Abandoned Carts Report" while
 * `AbandonedCarts.asp:80` heads the page "Abandoned Cart List" — singular, and no "Report". Both
 * are legacy's own strings and both are kept.
 */
export const STUDIO_REPORTS_ABANDONED_CARTS = `${STUDIO_REPORTS}/AbandonedCarts`;

/**
 * The abandoned cart list, optionally pinned to an age filter.
 *
 * ★ `minutes` IS A STRING OR A NUMBER, and `99999` is a SENTINEL meaning "all started carts" rather
 * than a duration (`AbandonedCarts.asp:37`) — see `use-abandoned-carts.ts`.
 */
export function reportsAbandonedCartsPath(minutes?: string | number): string {
    const value = typeof minutes === 'number' ? String(minutes) : minutes?.trim();

    return value
        ? `${STUDIO_REPORTS_ABANDONED_CARTS}?${new URLSearchParams({ minutes: value }).toString()}`
        : STUDIO_REPORTS_ABANDONED_CARTS;
}

/**
 * Audit Log Report — M1.5n (ROBO-255), replacing `Brand/Reports/AuditLog.asp` in its
 * `nOrderID`-EMPTY mode.
 *
 * ★ A STATIC CATALOGUE ENTRY, as above: `audit-log` is one of the rows in `report-catalog.ts`.
 *
 * ★ THIS IS THE STANDALONE REPORT, NOT THE ORDER PANEL. The same `.asp` file serves both; its
 * `nOrderID`-SET mode shipped as M1.5a and lives inside Order Details, reached from an order and
 * never from the Reports hub. Pointing the hub at that panel would hand the user a narrower report
 * than the one they asked for.
 *
 * ★ THE LABEL AND THE HEADING DISAGREE, and both are legacy's: the hub links it "Audit Log"
 * (`reports.asp`) while the page heads itself "Audit Log Report" (`AuditLog.asp:84`). Kept as-is,
 * like the three M1.5e siblings that do the same.
 */
export const STUDIO_REPORTS_AUDIT_LOG = `${STUDIO_REPORTS}/AuditLog`;

/* ═══════════════════════════════════════════════════════════════════════════════════════════════
 * M1.5m — the three App Integration reports (ROBO-258). All three are STATIC catalogue entries, so
 * these are plain constants; none of them takes a runtime key the way `reportsLegacyPath` does.
 *
 * ★ TWO OF THE THREE ARE CONDITIONALLY VISIBLE, and the condition stays in `report-catalog.ts`
 * where it already lives (`v.hasEsoftV2`, `v.hasMobileApp` — server-computed flags off
 * `GET reports/hub`, never re-derived from `user.roles`). A path constant is not a visibility rule,
 * and the API remains the enforcement layer either way.
 * ═══════════════════════════════════════════════════════════════════════════════════════════════ */

/**
 * Esoft Order Report — replacing `Brand/Reports/EsoftOrders.asp`.
 *
 * ★ THE ONE REPORT ON THIS PANEL WHOSE GATE IS USERTYPE ALONE (`:11` never checks
 * `brand-reports`), and the one whose brand scope is a FAMILY UNION rather than a plain equality
 * (`:18`). Both facts live server-side; the route carries neither.
 */
export const STUDIO_REPORTS_ESOFT_ORDERS = `${STUDIO_REPORTS}/EsoftOrders`;

/**
 * App Orders Placed Report — replacing `Brand/Reports/AppOrders.asp`.
 *
 * ★ THE PATH SEGMENT IS `AppOrdersPlaced`, NOT `AppOrders`. It matches the hub's label and the
 * page's own `<h2>` ("App Orders Placed Report", `:108`) rather than the legacy filename; the
 * catalogue id stays `app-orders`, which is what `report-catalog.ts` has always called it.
 *
 * ★ IT READS `start` AND `end` FROM THE QUERY STRING, unlike its two siblings — legacy posts them
 * (`:82`), but a shareable filtered URL is strictly better and costs nothing. ★ THERE IS NO SORT
 * PARAMETER: `nSort` is dead code in legacy (addendum §B) and the endpoint accepts none.
 */
export const STUDIO_REPORTS_APP_ORDERS_PLACED = `${STUDIO_REPORTS}/AppOrdersPlaced`;

/**
 * App Logins Report — replacing `Brand/Reports/AppLogins.asp`.
 *
 * ★ NO FILTER OF ANY KIND. The legacy page's only request read is the dead `nSort`, and its one
 * `<form>` is empty (`:53-54`). `backTo` is the only parameter this route reads.
 */
export const STUDIO_REPORTS_APP_LOGINS = `${STUDIO_REPORTS}/AppLogins`;

/* ═══════════════════════════════════════════════════════════════════════════════════════════════
 * M1.5h — the four Client Reports (ROBO-262). All four are STATIC catalogue entries, so these are
 * plain constants; none takes a runtime key the way `reportsLegacyPath` does.
 *
 * ★ TWO OF THE FOUR ARE CONDITIONALLY VISIBLE, and the conditions stay in `report-catalog.ts` where
 * they already live — `user-tiers` is `!isParentBrand` and `client-referrals` is `hasReferralAmount`
 * (`reports.asp:90-91`), both server-computed flags off `GET reports/hub`. ★★ NOTE THAT
 * `client-referrals` HAS NO `isParentBrand` CONDITION: it is shown to parent brands, which
 * additionally gain a "Business Name" column on it. A path constant is not a visibility rule, and
 * the API remains the enforcement layer either way.
 * ═══════════════════════════════════════════════════════════════════════════════════════════════ */

/**
 * Client List — replacing `Brand/Reports/ClientList.asp`.
 *
 * ★ IT READS `status`, `maxDays` AND `minDays` FROM THE QUERY STRING where legacy POSTs them
 * (`:149`) — a shareable filtered URL, which costs nothing and is strictly better. ★ THE THIRD
 * FILTER IS COUPLED TO THE SECOND server-side (`:23`), so the page shows what the API echoes rather
 * than what the URL asked for.
 */
export const STUDIO_REPORTS_CLIENT_LIST = `${STUDIO_REPORTS}/ClientList`;

/**
 * Client Performance Report — replacing `Brand/Reports/ClientPerformance.asp`.
 *
 * ★ THE ONE MIGRATED REPORT WITH A LIVE `sort` PARAMETER (`:25` clamps it and `:34` passes it to
 * `proc_ReportClientPerformance` as `@sort`) — the opposite of M1.5m's dead `nSort`. It is a SERVER
 * sort and it sits alongside the grid's own client-side column sort; the two are different features.
 */
export const STUDIO_REPORTS_CLIENT_PERFORMANCE = `${STUDIO_REPORTS}/ClientPerformance`;

/**
 * Clients on a Price Tier — replacing `Brand/Reports/UserTiers.asp`.
 *
 * ★ THE PATH SEGMENT IS `UserTiers`, matching the legacy filename and the catalogue id rather than
 * the label ("Clients on a Price Tier"), which does not make a URL segment. ★ THE ONE M1.5h REPORT
 * WHOSE BRAND PREDICATE IS A PLAIN EQUALITY (`:25`) while its three panel siblings are family
 * unions; that lives server-side and the route carries none of it.
 */
export const STUDIO_REPORTS_USER_TIERS = `${STUDIO_REPORTS}/UserTiers`;

/**
 * Client Referral Report — replacing `Brand/Reports/ClientReferrals.asp`.
 *
 * ★ `start` AND `end` ARE QUERY PARAMETERS here where legacy POSTs them (`:105`). ★ THE PAGE'S OWN
 * `<h2>` is "Client Referrals" while its `sPageTitle` says "Client Referral Report"; the catalogue
 * label follows the `<h2>`, as it does for the M1.5e/M1.5m siblings that disagree with themselves.
 */
export const STUDIO_REPORTS_CLIENT_REFERRALS = `${STUDIO_REPORTS}/ClientReferrals`;

/* ═══════════════════════════════════════════════════════════════════════════════════════════════
 * M1.5i (ROBO-256) — ACCOUNTS RECEIVABLE. Two STATIC catalogue entries, so each is a genuine
 * one-line `target` flip in `report-catalog.ts` (verified against that file rather than assumed —
 * M1.5c's section is built at runtime and its entries are not flips at all).
 *
 * ★★ THE TWO PAGES AGREE ON THE GATE AND DISAGREE ON ALMOST EVERYTHING ELSE. Both carry
 * `reports.asp:10` verbatim (`UserBalance:17`, `UnpaidInvoices:26`) and both scope to the brand
 * FAMILY — own brand plus active direct children, designed franchise inheritance rather than an
 * IDOR. From there they split: one clamps an unknown `nMode` and the other does not; one has an
 * Update button and the other submits on change; one is read-only and the other sends email.
 * ═══════════════════════════════════════════════════════════════════════════════════════════════ */

/**
 * Client Balance Report — replacing `Brand/Reports/UserBalance.asp`.
 *
 * ★ THE SEGMENT IS `ClientBalance`, matching the page's own `<h2>` (`:90`) and the catalogue label
 * rather than the legacy filename (`UserBalance.asp`) or its `sPageTitle` ("User Balance Report"),
 * which disagree with the heading. Same rule as `AppOrdersPlaced`.
 *
 * ★ `mode` IS A QUERY PARAMETER here, as it is in legacy (`:76`'s form is a GET). ★ A VALUE OUTSIDE
 * THE FOUR-MEMBER ENUM IS DROPPED BY THE PAGE rather than forwarded, because the endpoint 400s on
 * one — see `use-client-balance.ts`.
 */
export const STUDIO_REPORTS_CLIENT_BALANCE = `${STUDIO_REPORTS}/ClientBalance`;

/**
 * Unpaid Invoices — replacing `Brand/Reports/UnpaidInvoices.asp`.
 *
 * ★★★ THE ONLY MIGRATED REPORT THAT WRITES, AND ITS WRITE LEAVES THE SYSTEM: `POST
 * reports/unpaid-invoices/remind` SENDS REAL CUSTOMER EMAIL. ★★★ The route carries none of that —
 * the reminder is a POST from the page, never a URL — but a reader arriving at this constant should
 * know what is behind it before editing anything nearby.
 *
 * ★ `mode`, `days` AND `userId` ARE QUERY PARAMETERS here where legacy POSTs them (`:205`) — a
 * shareable filtered URL. ★ NONE OF THE THREE IS A TENANT SCOPING ID; the brand is a claim and the
 * family union is the guard, so a foreign `userId` yields an EMPTY report rather than another
 * brand's rows, exactly as legacy behaves.
 */
export const STUDIO_REPORTS_UNPAID_INVOICES = `${STUDIO_REPORTS}/UnpaidInvoices`;

/* ═══════════════════════════════════════════════════════════════════════════════════════════════
 * M1.5l — the four Catalog & Compliance reports (ROBO-257). All four are STATIC catalogue entries,
 * so these are plain constants; none takes a runtime key the way `reportsLegacyPath` does.
 *
 * ★ ONE OF THE FOUR IS CONDITIONALLY VISIBLE, and the condition stays in `report-catalog.ts` where
 * it already lives (`promo-code-usage` is `!isParentBrand`, from `reports.asp:115`). ★★ NOTE THE
 * GATE IS ON THE HUB LINK ONLY: `PromoCodeUsage.asp` itself never checks `BrandIsParent`, so a
 * parent-brand admin who types the URL gets the report — and the endpoint replicates that exactly,
 * flagged rather than tightened (addendum §F). A path constant is not a visibility rule, and the
 * API remains the enforcement layer either way.
 * ═══════════════════════════════════════════════════════════════════════════════════════════════ */

/**
 * Promo Code Usage Report — replacing `Brand/Reports/PromoCodeUsage.asp`.
 *
 * ★ NOT THE PROMO CODES CONFIGURATION PAGE (M2.7, `cart.asp?section=Promo+Codes`). This route reads
 * redemption history; that one owns the `tblPromo` rows it reports on.
 *
 * ★ IT READS `start`, `end` AND `promoId` FROM THE QUERY STRING, where legacy POSTs them (`:95`) —
 * a shareable filtered URL, as its M1.5m and M1.5h siblings do. ★★ `promoId` IS A **FILTER**, NOT A
 * SCOPING ID: the brand is a claim, and a promo id from another brand narrows the report to nothing
 * rather than widening it.
 */
export const STUDIO_REPORTS_PROMO_CODE_USAGE = `${STUDIO_REPORTS}/PromoCodeUsage`;

/**
 * Product List Report — replacing `Brand/Reports/ProductList.asp`.
 *
 * ★ NOT THE M2.1 PRODUCTS EDITOR. This is the read-only report; the editor is a different surface.
 * ★ NO PARAMETERS AT ALL — the legacy page's one `<form>` carries only its CSV button. `backTo` is
 * the only value this route reads.
 */
export const STUDIO_REPORTS_PRODUCT_LIST = `${STUDIO_REPORTS}/ProductList`;

/**
 * Expiring Domains — replacing `Brand/Reports/ExpiringDomains.asp`.
 *
 * ★★ NO PARAMETERS, AND THE FIXED `today − 7 … today + 40` WINDOW IS NOT ONE EITHER. It is computed
 * in SQL and the legacy page has no filter form; adding a date picker would invent a filter surface
 * legacy does not have (addendum §D.3). `backTo` is the only value this route reads.
 *
 * ★ THE ONE REPORT ON THIS PANEL WHOSE BRAND SCOPE IS A FAMILY UNION (`:25`) — own brand ∪ ALL
 * direct children, with no active-status test on the child arm. Server-side; the route carries none
 * of it.
 */
export const STUDIO_REPORTS_EXPIRING_DOMAINS = `${STUDIO_REPORTS}/ExpiringDomains`;

/**
 * Team Member Agreement Acceptance List — replacing `Brand/Reports/AgreementAccepts.asp`.
 *
 * ★ `days` IS A QUERY PARAMETER here where legacy GETs it as `nDays` (`:57`). ★★ AND AN
 * OUT-OF-RANGE VALUE IS A 400, NOT A SILENT 90 — see `use-agreement-accepts.ts`; the page renders
 * the refusal rather than assuming the filter always succeeds.
 *
 * ★ THE ONE M1.5l REPORT WITH NO CSV AT ALL: the legacy file has no `csv` branch anywhere.
 */
export const STUDIO_REPORTS_AGREEMENT_ACCEPTS = `${STUDIO_REPORTS}/AgreementAccepts`;

/**
 * Order Task Report — M1.5f (ROBO-264), replacing `Brand/Reports/OrderTask.asp`.
 *
 * ★ IT READS `revenueMode`, `from`, `to`, `groupId`, `productId`, `includeLineDetail` AND
 * `includeWorkflowTasks` FROM THE QUERY STRING, where legacy POSTs them (`:293`) — a shareable
 * filtered URL, as its M1.5l and M1.5m siblings have.
 *
 * ★★ NEITHER `groupId` NOR `productId` IS A SCOPING ID. The brand is a claim and is not a parameter
 * in any form; both ids are FILTERS inside a statement already bounded by the family predicate
 * (`:172`), so an id from outside the caller's family narrows the report to nothing and can never
 * widen it. That is why the endpoint accepts them without a brand check, exactly as legacy does — a
 * 403 there would be a new existence oracle (addendum §B.4).
 *
 * ★ THE ONE REVENUE-SECTION ENTRY THAT SURVIVES `isParentBrand` in the hub catalogue, and the first
 * of that section to go native.
 */
export const STUDIO_REPORTS_ORDER_TASK = `${STUDIO_REPORTS}/OrderTask`;

/**
 * Site Report — M1.5g (ROBO-261), replacing `Brand/Reports/SiteActivations.asp`.
 *
 * ★ THE ROUTE SEGMENT FOLLOWS THE PAGE'S OWN HEADING AND THE HUB LABEL ("Site Report"), NOT the
 * legacy filename. The catalogue `id` stays `site-activations` — that is the legacy key and the
 * `:reportKey` segment that must keep redirecting to the hub. All three are allowed to differ.
 *
 * ★ IT READS `dateMode`, `start` AND `end` FROM THE QUERY STRING, where legacy POSTs them (`:141`).
 * ★★ NONE OF THE THREE IS A SCOPING ID — the brand is a claim and appears in no parameter, and this
 * report's scope is a plain `s.nBrandID` equality (`:71`), not a family union.
 *
 * ★★ THE COLUMN SET IS DYNAMIC (32/36/37 columns) AND ARRIVES WITH THE DATA. Nothing in the route or
 * the page hardcodes one.
 */
export const STUDIO_REPORTS_SITE_REPORT = `${STUDIO_REPORTS}/SiteReport`;

/**
 * Product Performance — M1.5g (ROBO-261), replacing `Brand/Reports/ProductPerformance.asp`.
 *
 * ★★★ THE ONE REPORT IN THIS SLICE WITH A TOTALS ROW, computed CLIENT-SIDE from the returned rows
 * exactly as legacy accumulates it inside its render loop (`:216-217`). The endpoint ships no total
 * and no row cap, deliberately (addendum §3.1).
 *
 * ★ IT READS `sort`, `assignUserId`, `packageMode`, `start` AND `end` FROM THE QUERY STRING, where
 * legacy POSTs them (`:138`). ★★ `assignUserId` IS A FILTER, NOT A SCOPING ID: it is ANDed onto two
 * statements already bounded by a brand equality, so a foreign user id narrows the grid to nothing.
 */
export const STUDIO_REPORTS_PRODUCT_PERFORMANCE = `${STUDIO_REPORTS}/ProductPerformance`;

/**
 * Upsell Performance — M1.5g (ROBO-261), replacing `Brand/Reports/UpsellOrders.asp`.
 *
 * ★ THE ROUTE SEGMENT FOLLOWS THE PAGE'S `<h2>` AND THE HUB LABEL ("Upsell Performance"), not the
 * legacy filename and not its `sPageTitle` ("Upsell Orders"). The catalogue `id` stays
 * `upsell-orders`.
 *
 * ★★★ NO TOTALS ROW, DELIBERATELY — `UpsellOrders.asp:153`, `:158` and `:159` are commented out
 * (addendum §3.2). Its slice sibling above has one; this does not.
 *
 * ★ IT READS `sort`, `start` AND `end` FROM THE QUERY STRING, where legacy POSTs them (`:118`), and
 * carries no entity id of any kind.
 */
export const STUDIO_REPORTS_UPSELL_PERFORMANCE = `${STUDIO_REPORTS}/UpsellPerformance`;

/**
 * Paid Invoices — M1.5g (ROBO-261) part B, replacing `Brand/Reports/PaidInvoices.asp`.
 *
 * ★★★ THE ONE REPORT IN PART B WITH A TOTALS ROW, computed CLIENT-SIDE from the returned rows
 * exactly as legacy accumulates it inside its render loop (`:167`). The endpoint ships no total and
 * no row cap, deliberately (addendum §3.3). Its two part-B siblings have NO footer.
 *
 * ★ IT READS `mode`, `start` AND `end` FROM THE QUERY STRING, where legacy POSTs them (`:92`), and
 * carries no entity id of any kind. ★★ THE MODE SELECT AUTO-SUBMITS (`:97`), the only control in
 * this panel that does.
 *
 * ★ THE `Invoice` COLUMN'S "View" ACTION IS OUT OF SCOPE — `hd.invoiceView` is ROBO-49 / M1.5i-2.
 */
export const STUDIO_REPORTS_PAID_INVOICES = `${STUDIO_REPORTS}/PaidInvoices`;

/**
 * Client Tips Received — M1.5g (ROBO-261) part B, replacing `Brand/Reports/ClientTips.asp`.
 *
 * ★★★ NO TOTALS ROW, AND NONE MAY BE ADDED: the `CROSS APPLY` (`:37-44`) emits one row per tip LINE
 * and repeats the whole order's total on each, so a footer over `Order Total` would over-count by
 * exactly the ROBO-332 mechanism (addendum §3.4).
 *
 * ★ IT READS `start` AND `end` FROM THE QUERY STRING, where legacy GETs them (`:108`) — the only two
 * request values the page reads. ★★ `Site ID` IS A HIDDEN COLUMN used only to build the property
 * link; it is not part of the route, the grid or the CSV.
 */
export const STUDIO_REPORTS_CLIENT_TIPS = `${STUDIO_REPORTS}/ClientTips`;

/**
 * Stripe Payment Reconciliation — M1.5g (ROBO-261) part B, replacing
 * `Brand/Reports/StripePayouts.asp`.
 *
 * ★ THE ROUTE SEGMENT FOLLOWS THE HUB LABEL ("Stripe Payment Reconciliation"), not the legacy
 * filename and not its `<h2>` (which ends in "Report"). The catalogue `id` stays `stripe-payouts`.
 *
 * ★★★ NO OUTBOUND STRIPE CALL IN ANY CODE PATH. `[Stripe Payout ID]` arrives null on every row and
 * is rendered as an explicit "—" with an explanation; the enrichment is deferred (addendum §7.1).
 *
 * ★★★ A 403 IS A FIRST-CLASS STATE, not an error: the brand's active payment processor must be
 * Stripe (`:22-25`). It is told apart from the shared `brand-reports` 403 by its message.
 *
 * ★★★ NO TOTALS ROW, on the report whose purpose is reconciling money — legacy renders none (§3.5).
 *
 * ★ IT READS `start` AND `end` FROM THE QUERY STRING, where legacy POSTs them (`:167`); no entity id
 * of any kind, and the brand claim roots the family union on both statement arms AND the gate.
 */
export const STUDIO_REPORTS_STRIPE_RECONCILIATION = `${STUDIO_REPORTS}/StripeReconciliation`;

/* ═══════════════════════════════════════════════════════════════════════════════════════════════
 * M1.5k (ROBO-260) — Business Reports I / Purchases & Payments. THREE routes, not four.
 *
 * ★★ TEAM MILEAGE WAS THE FOURTH AND WAS DELIBERATELY NOT ROUTED BY THIS STEP. `TeamMileage.asp` is
 * not a read-only report: its render loop calls `VerifyRoutes` per team member, which geocodes,
 * `UPDATE`s `tblSite`/`tblUserEx`, `INSERT`s `tblRoute` and makes billed AWS Location Services
 * calls. It needed a submit/poll job primitive this codebase did not have, so it was split out
 * (addendum §0) — and M1.5k-2 (ROBO-254) has since built that and routed it at
 * {@link STUDIO_REPORTS_TEAM_MILEAGE}, below.
 * ═══════════════════════════════════════════════════════════════════════════════════════════════ */

/**
 * Pay Discrepancy — replacing `Brand/Reports/PayDiscrepancy.asp`.
 *
 * ★★ NO PARAMETERS OF ANY KIND, AND NONE MAY BE ADDED. The legacy file reads no `Request()` value
 * anywhere — no date window, no user filter, no mode, no id — and has no `<form>` and no CSV branch.
 * `backTo` is the only value this route reads.
 *
 * ★★ ITS GATE IS `cart` OR `cart-transfer`, NOT `brand-reports` — the one M1.5k report whose page
 * gate differs from its two siblings (`:10`). ★ AND THE HUB HIDES THE LINK MORE OFTEN THAN THE PAGE
 * REFUSES IT (`reports.asp:111` also excludes a child brand carrying `parent-payteam`), so this
 * route is legitimately reachable by URL for someone who sees no link — exactly as in legacy.
 */
export const STUDIO_REPORTS_PAY_DISCREPANCY = `${STUDIO_REPORTS}/PayDiscrepancy`;

/* ═══════════════════════════════════════════════════════════════════════════════════════════════
 * M1.5k-2 (ROBO-254) — Team Mileage, split out of M1.5k because the legacy page is not a report.
 * ═══════════════════════════════════════════════════════════════════════════════════════════════ */

/**
 * Team Mileage — replacing `Brand/Reports/TeamMileage.asp`.
 *
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * ★★★ ITS OWN STEP BECAUSE THE LEGACY PAGE SPENDS MONEY EVERY TIME IT IS LOOKED AT. ★★★
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 *
 * `TeamMileage.asp` calls `VerifyRoutes` once per visible team member on every render (`:169`) and
 * again for the CSV (`:53`): geocode calls per un-located site, an AWS Location Services routing POST
 * per uncached day, `UPDATE tblSite`, `UPDATE tblUserEx`, `INSERT tblRoute` — all inside a GET.
 * Behind this route the read is CACHE-ONLY and the outbound work is an explicit, confirmed button
 * (`POST reports/team-mileage/refresh` → poll). ★★ A DELIBERATE, OBSERVABLE DIVERGENCE: a window
 * whose appointments are new shows cached (possibly zero) distances until a refresh has run.
 *
 * ★★ THE ENDPOINTS CARRY THE SIBLING REPORTS GATE THE LEGACY PAGE ALONE OMITS. `:1-3` includes only
 * `inc/secure.asp` ("is anyone logged in"), so today a client or an assistant can read the whole team
 * roster and spend the brand's AWS budget doing it (ROBO-113, High).
 *
 * ★ IT READS `start`, `end` AND `units` FROM THE QUERY STRING, where legacy POSTs `sStart`/`sEnd`/
 * `nUnits` (`:16-18`). ★★ `units` OUTSIDE `{1,2}` IS A 400, deliberately neither of the two things
 * legacy does — its arithmetic reads anything-not-`"1"` as kilometres while its dropdown displays
 * "Miles" for anything-not-`"2"` (spec §6.3, §7 row 8).
 *
 * ★★ NO SCOPING ID OF ANY KIND on any of the three endpoints. The brand is a claim, and there is no
 * `userIds` filter on the refresh — spec §9 sketches one and it was deliberately not built, since a
 * caller-supplied user-id set on the endpoint that spends money would be a scoping id needing its own
 * resource policy.
 *
 * ★ THE CATALOGUE ROW STAYS HIDDEN FOR PARENT BRANDS (`reports.asp:109`), while the route itself is
 * reachable by URL — replicating legacy's own inconsistency, which has no `BrandIsParent()` check in
 * the page (spec §7 row 20).
 */
export const STUDIO_REPORTS_TEAM_MILEAGE = `${STUDIO_REPORTS}/TeamMileage`;

/**
 * Manual Payouts — replacing `Brand/Reports/ManualPayments.asp`.
 *
 * ★ THE SEGMENT FOLLOWS THE PAGE'S OWN `<h2>`/`sPageTitle` ("Manual Payouts Report", `:61`/`:134`),
 * not the legacy filename and not the hub label ("Manual Payments"). The catalogue `id` stays
 * `manual-payments`.
 *
 * ★ IT READS `start`, `end` AND `userId` FROM THE QUERY STRING, where legacy POSTs them (`:102`).
 * ★★ `userId` IS A FILTER, NOT A SCOPING ID — the brand is a claim and the grid's own equality is
 * the guard, so a foreign id returns an EMPTY report rather than someone else's. ★★★ AND SO CAN A
 * PERFECTLY LEGITIMATE ONE: the dropdown that offers the ids is a family ∪ `tblUserAux` union while
 * the grid is a plain brand equality, so child-brand and aux members are selectable and match
 * nothing. Legacy's; do not "fix" it by narrowing the dropdown or by warning about it.
 */
export const STUDIO_REPORTS_MANUAL_PAYOUTS = `${STUDIO_REPORTS}/ManualPayouts`;

/**
 * Purchase History — replacing `Brand/Reports/Purchases.asp`.
 *
 * ★ NOT the billing screen at `src/pages/billing/reports/purchase-history.tsx`, which reads a
 * different feed for a different audience. Keep the `Reports/` segment.
 *
 * ★ IT READS `mode`, `start` AND `end` FROM THE QUERY STRING, where legacy GETs them (`:123`).
 * ★★ `mode` IS 1, 2 OR 3 AND ANYTHING ELSE IS A 400, NOT A SILENT 1 — legacy falls back and then
 * honestly re-stamps its dropdown, but clamping a filter on a money report is a silent wrong answer
 * (addendum §3.1). Absent or empty still means 1.
 *
 * ★★ THE HUB ROW CORRECTLY CARRIES NO `isParentBrand` PREDICATE, and the ticket that said otherwise
 * was wrong: `reports.asp:108` is unguarded, and this report explicitly ADDS two columns for a
 * parent brand (`:164`, `:166`). It is built for franchisors.
 *
 * ★★★ ITS MODE-1 `Balance` COLUMN IS COMPUTED ON THE CLIENT from a scalar seed, and reproduces a
 * legacy money defect (ROBO-311) on purpose — see `purchase-history-model.ts`.
 */
export const STUDIO_REPORTS_PURCHASE_HISTORY = `${STUDIO_REPORTS}/PurchaseHistory`;

/* ═══════════════════════════════════════════════════════════════════════════════════════════════
 * M1.5j (ROBO-259) — Business Reports: Monthly Billing.
 * ═══════════════════════════════════════════════════════════════════════════════════════════════ */

/**
 * Monthly Billing — replacing the three READ paths of `Brand/Reports/MonthlyBilling.asp`.
 *
 * ★★ ONE ROUTE, THREE VIEWS, SELECTED BY `view` — `brands` (legacy `nMode=1`) and `transactions`
 * (`nMode=2`), with an absent value meaning the summary (`nMode` omitted). `year` and `month` are
 * REQUIRED by both windowed views and are neither defaulted nor clamped: out of `1753…9999` /
 * `1…12` the endpoint 400s, because a clamp silently rewrites a filter on a money report and legacy
 * itself 500s on anything outside those bounds (addendum §G). `brandId` is optional on
 * `transactions` and OMITTED MEANS OMITTED.
 *
 * ★★ `brandId` IS A NARROWING FILTER, NOT A SCOPING ID. Every row is already bounded by
 * `c.nBrandID = <claim>` (`:419`), unconditionally, so the parameter can only ever REMOVE rows — it
 * is deliberately given no relationship check, which would narrow legacy without closing anything
 * (addendum §D).
 *
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * ★★★ THIS PAGE STILL CONTAINS ONE `.asp` LINK, ON PURPOSE. ★★★
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 *
 * The summary's `View Invoice` links keep targeting
 * `/Brand/Reports/MonthlyBilling.asp?nQboInvoice=<id>` in a new tab (`:325`). The PDF behind them is
 * a live outbound GET to `quickbooks.api.intuit.com` using brand 1's PLATFORM credential, and the
 * API has a QuickBooks *Payments* client only — deferred to its own ticket (addendum §E), not
 * finished here. ★ AND `createinvoices` (`:33-212`) is out of scope entirely (§F): nothing on this
 * route can reach it.
 */
export const STUDIO_REPORTS_MONTHLY_BILLING = `${STUDIO_REPORTS}/MonthlyBilling`;

/* ═══════════════════════════════════════════════════════════════════════════════════════════════
 * M1.5o (ROBO-47) — Sales Tax Collection, SLICE 1.
 * ═══════════════════════════════════════════════════════════════════════════════════════════════ */

/**
 * Sales Tax Collection — `Brand/Reports/SalesTax.asp` mode 1, reads AND writes.
 *
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * ★★★ THE REPORTS HUB NOW LINKS HERE — FLIPPED BY SLICE 3 (ROBO-51), AND ONLY THEN. ★★★
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 *
 * Slice 1 deliberately left `report-catalog.ts` on the LEGACY arm, because the legacy page was the
 * only way a brand could COMMIT a tax period and the commit/uncommit workflow did not exist here
 * yet (spec §11). Flipping before slice 3 would have removed a financial workflow. Slice 3 built
 * both writes, so the entry is now `{ kind: 'native', path: STUDIO_REPORTS_SALES_TAX_COLLECTION }`.
 *
 * ★★★ THE FLIP HAS A DEPLOY-ORDER DEPENDENCY. The write endpoints
 * (`POST reports/sales-tax/commit`, `DELETE reports/sales-tax/reports/{id}`) ship from
 * HDPhotoHubAPI. If this UX reaches an environment where that API has NOT been deployed, the hub
 * sends every user to a page whose commit and uncommit both 404 — which is exactly the harm the
 * slice-1 sequencing decision existed to prevent, just moved from code to deployment. **The API
 * must be deployed and verified before this flip goes live.**
 *
 * ⚠ IT IS NOT `${STUDIO_ORDER_FORM}/SalesTax`, the tax-RATE CONFIGURATION screen M2.3 shipped. Two
 * different legacy files, repeatedly confused on this project (spec §0). Keep the `Reports/` segment.
 *
 * ★ IT READS `cutoffDate` FROM THE QUERY STRING, where legacy POSTs `sCutoffDate` (`:237`), and it is
 * the ONLY request value slice 1 reads. ★★ IT NARROWS THE UNCOMMITTED GRID ONLY — the history
 * statement (`:80-104`) carries no cutoff predicate and must not gain one.
 *
 * ★★ NO SCOPING ID OF ANY KIND, and here that matters more than usual: legacy's commit re-parents
 * CALLER-SUPPLIED `nOrderTaxID` values with no brand check (spec §3.1). Neither endpoint behind this
 * route returns that id set, and slice 1 posts nothing.
 */
export const STUDIO_REPORTS_SALES_TAX_COLLECTION = `${STUDIO_REPORTS}/SalesTax`;

/**
 * One client's detail page — the native screen that replaced `Clients/client.asp?nUserID=`.
 *
 * ★ ADDED BY M1.5h, WHICH NEEDED IT FOR TWO REPORT CELLS. `ClientPerformance.asp:204` and
 * `UserTiers.asp:86` both link their client-name column at the legacy `.asp`; a new `.asp` reference
 * is forbidden in new code, and `Clients/:clientId` is the route that already serves that screen
 * (`studio-routes.tsx`). Several older pages hardcode this string inline — they predate the
 * constant, and this exists so new callers stop doing that.
 *
 * ★★ D47 (2026-08-22) — RATIFIED. Sending report client-name cells to the native screen rather than
 * to `Clients/client.asp?nUserID=` is a deliberate deviation from legacy, reviewed as such and kept.
 * It is already what all five linking reports do — client-balance, client-performance,
 * expiring-domains, manual-payouts, user-tiers — and it is effectively the only option: a new `.asp`
 * reference is forbidden, and the legacy screen this would target has already been migrated, so
 * linking to it would push users out of the React app and into a page we are trying to retire.
 * The decision was previously implicit in each page's own comment; it is recorded once here.
 */
export function studioClientPath(clientId: number | string): string {
    return `${STUDIO_BASE}/Clients/${clientId}`;
}

/** Public delivery experience (MPA entry: ux/delivery/index.html). */
export function deliverySitePath(siteId: string, tab = 'photos-media'): string {
    return `/ux/delivery/${siteId}/${tab}`;
}

/**
 * The legacy admin Brand Edit page (`Admin/Brand/brand.asp`). There is no React page for ANOTHER
 * brand's details — `Business/Details` is always the signed-in brand — so every caller that opens
 * one lands here.
 *
 * ★ BUILT WITH `URLSearchParams`, NOT INTERPOLATION. `sBackToList` carries a PATH in a query VALUE,
 * and its raw slashes made the server reject the whole request: UX-826 measured
 * `…&sBackToList=/ux/studio/Businesses` as a 404 where the same URL with the value percent-encoded
 * served the page normally. Three call sites built that string by hand — the Businesses list, the
 * Businesses grid and the dashboard's Business Name column — and all three had been landing on it.
 * This exists so there is one place that gets the encoding right.
 *
 * `backToList` is legacy's own return-path param, read by `inc/begin.asp:501` and dropped RAW into
 * an `href` there (ROBO-101), so it must never carry a caller-controlled value — every call site
 * passes one of our own static routes.
 */
export function adminBrandPath(brandId: number | string, backToList: string): string {
    const params = new URLSearchParams({
        nBrandID: String(brandId),
        sBackToList: backToList
    });
    return `/Admin/Brand/brand.asp?${params.toString()}`;
}
